// ── 일일콜현황 메인 컴포넌트 ──────────────────────────────────────────────────
// 상태:    callregister/useCallRegisterCore
// 이펙트:  callregister/useCallRegisterHandlers
// 액션:    callregister/useCallRegisterSubmit
// 서브컴:  callregister/{RegistrationForm, CallList, EditModal, KeepReleaseModal}
//
// Props:
//   adminMode:   true이면 관리자 페이지 (기본 false)
//   scope:       undefined(전체) | 'jeju'(제주 내부직원)
//   companyCode: 외부 회사 코드 (예: 'DOT') — adminMode=true 시 회사별 전용 관리
//   companyName: 외부 회사 표시명 (헤더용)
//   integratedMenu: { id, companyCodes } — 통합 일일콜 메뉴(2026-10-13, 통합 계획 3단계 5번). 있으면
//     주소 = /api/admin/integrated-menus/{id}/tm-calls, 등록할 회사를 화면에서 고른다(그 회사 상품).
//     목록에는 회사를 따로 표시하지 않는다 — 지금 '(통합)' 칸과 같다(2026-10-14 결정 8-2 a).
//     등록할 회사는 담당자를 고르면 그 사람 회사로 저절로 맞춰지고, 다른 회사 콜일 때만 손으로 바꾼다(2026-10-14 결정).
//   pageTitle: 통합 메뉴 제목(없으면 '일일콜현황 관리')

import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../../components/Toast';
import { useCallRegisterCore }    from './callregister/useCallRegisterCore';
import { useCallRegisterHandlers } from './callregister/useCallRegisterHandlers';
import { useCallRegisterSubmit }  from './callregister/useCallRegisterSubmit';
import { RegistrationForm }       from './callregister/RegistrationForm';
import { CallList }               from './callregister/CallList';
import { EditModal }              from './callregister/EditModal';
import { KeepReleaseModal }       from './callregister/KeepReleaseModal';
import CallerPendingFeedback      from './components/CallerPendingFeedback';
import FeedbackEntryModal         from './components/FeedbackEntryModal';
import { useFeedbackEdit }        from './callregister/callRegisterFeedbackActions';
import { pageHeaderStyle, pageTitleStyle, pageSubtitleStyle, todayBadgeStyle } from './callregister/listStyles';
// 관리자 헤더 표준 토큰 (직원 관리 기준 디자인 — adminMode 분기 안에서만 사용)
import { stdHeaderRow, stdPageTitle, stdPageSubtitle } from '../admin/adminlayout/designStandard';
// 일일콜 관리 제목: 통합/본사 이름 규칙 (2026-09-24, 사용자 확정)
import { useTenantMenuInfo } from '../admin/adminlayout/tenantMenuInfo';
import { callPageHeader } from '../admin/adminlayout/callMenuNaming';
import { CallCompanyPicker } from './callregister/CallCompanyPicker';

// scope: undefined(기본/전체) | 'jeju'(제주-내부직원)
// companyCode가 설정되면 scope='movie' 역할을 대체합니다 (카테고리명 고정 + 회사별 API 사용)
export default function CallRegister({ adminMode = false, scope, companyCode, companyName, integratedMenu = null, pageTitle = null }) {
  const { showToast } = useToast();
  const tenantInfo = useTenantMenuInfo();
  // 통합 일일콜 메뉴: 등록할 회사(비어 있으면 등록 막힘). 담당자를 고르면 그 사람 회사로 맞춘다(아래 이펙트)
  const integrated = Boolean(integratedMenu?.id);
  const menuCompanies = integrated
    ? (integratedMenu.companyCodes || []).map((c) => String(c || '').trim().toUpperCase()).filter(Boolean) : [];
  const [pickedCompany, setPickedCompany] = useState('');
  const callCompany = integrated ? pickedCompany : companyCode;
  // 회사별 화면(companyCode)은 종전 제목. 통합(A)·본사(jeju) 화면만 규칙을 따른다. null = 종전 문구.
  const namedHeader = (adminMode && !companyCode && !integrated) ? callPageHeader(scope === 'jeju' ? 'hq' : 'all', tenantInfo) : null;

  // 관리자 API base path 결정
  // companyCode 있으면 → /api/admin/ext/{code}/tm-calls (회사별 전용)
  // scope='jeju' → /api/admin/tm-jeju
  // 없음 → /api/admin/tm
  const adminApiBase = integrated ? `/api/admin/integrated-menus/${integratedMenu.id}/tm-calls`
    : companyCode ? `/api/admin/ext/${companyCode}/tm-calls`
    : scope === 'jeju' ? '/api/admin/tm-jeju'
    : '/api/admin/tm';

  // ── 상태 ────────────────────────────────────────────────────────────────
  // companyCode 를 넘기는 이유: 폼 검증이 "어느 회사 콜인가" 를 알아야 한다.
  //   상품 칸이 카테고리 고정으로 잠기는 회사에서 검증이 상품을 요구하면 등록이 막힌다.
  const core = useCallRegisterCore(adminMode, callCompany);

  // 외부 회사 관리 모드 + 카테고리 고정 여부: is_category=true일 때만 readOnly 적용
  // is_category=false(상품 선택 모드)이면 드롭다운 표시 (core 이후에 정의해야 함)
  const isMovieAdminMode = adminMode && Boolean(callCompany) && core.isExtCategoryFixed;

  // ── 이펙트 + fetch ────────────────────────────────────────────────────────
  const handlers = useCallRegisterHandlers(core, {
    adminMode, adminApiBase, scope, companyCode: callCompany, showToast, integrated,
  });

  // ── 제출/수정/삭제/킵 ─────────────────────────────────────────────────────
  const callsUrl = integrated ? `${adminApiBase}/calls` : '/api/tm/calls';
  const actions = useCallRegisterSubmit(core, {
    adminMode, adminApiBase, isMovieAdminMode, companyCode: callCompany,
    callsUrl,
    integrated,
    showToast,
    fetchList:   handlers.fetchList,
    nextBizDate: handlers.nextBizDate,
    upsertPref:  handlers.upsertPref,
  });
  // 목록 결과 ✎ 고치기·지우기(2026-11-03) — 직원 화면은 결과 저장 주소, 관리 화면은 이 화면의 콜 저장 주소
  const feedbackEdit = useFeedbackEdit({ adminMode, callsUrl, showToast, fetchList: handlers.fetchList });

  // ── 구조분해 (JSX 가독성) ─────────────────────────────────────────────────
  const {
    isMobile, loading, ticketTypes, isExternalWorker, isExtCategoryFixed, currentExternalCategoryName,
    form, setForm, naverText, setNaverText,
    formContentRef, saving, submitResultText,
    listTemplate, listMinWidth, visibleListColumns,
    filteredList, filterKeep, setFilterKeep, filterQuery, setFilterQuery,
    ownerOptions, fixedSalesPerson, setFixedSalesPerson,
    editOpen, setEditOpen, editTarget, editForm, setEditForm, editSaving, deletingId,
    keepReleaseOpen, setKeepReleaseOpen, keepReleaseDate, setKeepReleaseDate,
    keepReleaseSales, setKeepReleaseSales, keepReleaseSaving,
    keepReleaseMemo, setKeepReleaseMemo, keepReleaseActions,
    salesPersonNames, editContentRef, todayKstStr, todayCount, todayLabel,
  } = core;

  const { onResizeStart } = handlers;
  const {
    onPasteNaver, onSubmit, onEditSave, onDelete,
    openKeepReleaseModal, onKeepMemoSave, onKeepReleaseAssign, onKeepReleaseCancelVisit,
  } = actions;
  // 통합 메뉴: 담당자를 고르면 등록할 회사를 그 사람 회사로 맞춘다(사용자 결정 2026-10-14 — 필요할 때만 손으로 바꾸기).
  //   파인그룹은 콜 2,400건 중 2,399건이 '담당자 회사 = 콜 회사'. 유기자차는 직원이 모두 SUNCREAM 이라
  //   SUNCREAM2 콜은 버튼으로 바꾼다. 담당자가 그대로면 손으로 바꾼 회사를 유지한다. 메뉴 밖 회사면 건드리지 않는다.
  const ownerCompany = integrated
    ? (ownerOptions.find((o) => String(o.id) === String(form.owner_user_id))?.company_code || '') : '';
  useEffect(() => {
    if (ownerCompany && menuCompanies.includes(ownerCompany)) setPickedCompany(ownerCompany);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.owner_user_id, ownerCompany]);

  // 통합 메뉴: 다른 회사 콜을 고치면 상품 목록을 그 콜의 회사로 맞춘다(사용자 결정 — 콜 회사 기준으로 다시 읽기).
  //   수정 창을 닫으면 등록 칸의 회사를 열기 전으로 되돌린다(담당자 회사와 어긋난 채 남지 않게).
  const companyBeforeEdit = useRef(null);
  const openEditModal = (row) => {
    const c = String(row?.company_code || '').trim().toUpperCase();
    if (integrated && !row?.is_contracted && c && menuCompanies.includes(c) && c !== pickedCompany) {
      if (companyBeforeEdit.current === null) companyBeforeEdit.current = pickedCompany;
      setPickedCompany(c);
    }
    return actions.openEditModal(row);
  };
  useEffect(() => {
    if (editOpen || companyBeforeEdit.current === null) return;
    setPickedCompany(companyBeforeEdit.current);
    companyBeforeEdit.current = null;
  }, [editOpen]);

  // ── 렌더 ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* 직원 전용 페이지 헤더 */}
      {!adminMode && (
        <div style={pageHeaderStyle(isMobile)}>
          <div>
            <div style={pageTitleStyle}>일일콜현황</div>
            <div style={pageSubtitleStyle}>{todayLabel}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* 상담자 피드백 대기: 마케터가 전화로 전달한 미팅 결과를 여기서 바로 입력 */}
            <CallerPendingFeedback showToast={showToast} refreshKey={feedbackEdit.refreshKey} onSaved={handlers.fetchList} />
            <div style={todayBadgeStyle}>오늘 <strong>{todayCount}</strong>건</div>
          </div>
        </div>
      )}
      {adminMode && (
        <div style={stdHeaderRow(isMobile)}>
          <div>
            <h2 style={stdPageTitle}>
              {integrated ? (pageTitle || '일일콜현황 관리')
                : companyName ? `일일콜현황 관리 (${companyName})` : (namedHeader?.title || '일일콜현황 관리')}
            </h2>
            <div style={stdPageSubtitle}>
              {integrated
                ? `${menuCompanies.join(' · ')} 일일 콜 기록을 함께 관리합니다.`
                : companyCode
                  ? `${companyName || companyCode} 일일 콜 기록을 관리합니다.`
                  : (namedHeader?.subtitle || '전체 일일 콜 기록을 관리합니다.')}
            </div>
          </div>
        </div>
      )}

      {/* ── 통합 메뉴: 등록할 회사(담당자 회사로 저절로 맞춰짐, 다를 때만 바꿈) ── */}
      {integrated && (
        <CallCompanyPicker companies={menuCompanies} value={pickedCompany} onChange={setPickedCompany} isMobile={isMobile} />
      )}

      {/* ── 등록 폼 ── */}
      <RegistrationForm
        adminMode={adminMode} isMobile={isMobile}
        companyCode={callCompany} isExternalWorker={isExternalWorker} isExtCategoryFixed={isExtCategoryFixed} isMovieAdminMode={isMovieAdminMode}
        form={form} setForm={setForm} naverText={naverText} setNaverText={setNaverText}
        onPasteNaver={onPasteNaver}
        ticketTypes={ticketTypes} currentExternalCategoryName={currentExternalCategoryName}
        ownerOptions={ownerOptions} fixedSalesPerson={fixedSalesPerson} setFixedSalesPerson={setFixedSalesPerson}
        formContentRef={formContentRef} saving={saving} onSubmit={onSubmit} submitResultText={submitResultText}
      />

      {/* ── 콜 목록 ── */}
      <CallList
        adminMode={adminMode} isMobile={isMobile} loading={loading}
        filteredList={filteredList} filterKeep={filterKeep} setFilterKeep={setFilterKeep}
        filterQuery={filterQuery} setFilterQuery={setFilterQuery}
        pagedList={core.pagedList} listPage={core.listPage} listTotalPages={core.listTotalPages} setListPage={core.setListPage}
        listTemplate={listTemplate} listMinWidth={listMinWidth} visibleListColumns={visibleListColumns}
        deletingId={deletingId} openEditModal={openEditModal}
        onDelete={onDelete} openKeepReleaseModal={openKeepReleaseModal}
        onResizeStart={onResizeStart} onFeedbackEdit={feedbackEdit.open}
      />

      {/* ── 결과 고치기·지우기 창 ── */}
      <FeedbackEntryModal
        target={feedbackEdit.target} mode="edit" canEdit={feedbackEdit.canEdit} saving={feedbackEdit.saving}
        onClose={feedbackEdit.close} onSave={feedbackEdit.save} onDelete={feedbackEdit.remove}
      />

      {/* ── 수정 모달 ── */}
      <EditModal
        editOpen={editOpen} setEditOpen={setEditOpen}
        editTarget={editTarget} editForm={editForm} setEditForm={setEditForm}
        editSaving={editSaving} deletingId={deletingId}
        adminMode={adminMode} isExternalWorker={isExternalWorker}
        isExtCategoryFixed={isExtCategoryFixed} companyCode={callCompany} isMovieAdminMode={isMovieAdminMode}
        ticketTypes={ticketTypes} currentExternalCategoryName={currentExternalCategoryName}
        salesPersonNames={salesPersonNames} editContentRef={editContentRef}
        onEditSave={onEditSave} onDelete={onDelete} openKeepReleaseModal={openKeepReleaseModal}
        showToast={showToast} todayKstStr={todayKstStr}
      />

      {/* ── 킵 해제 모달 ── */}
      <KeepReleaseModal
        keepReleaseOpen={keepReleaseOpen} setKeepReleaseOpen={setKeepReleaseOpen}
        keepReleaseDate={keepReleaseDate} setKeepReleaseDate={setKeepReleaseDate}
        keepReleaseSales={keepReleaseSales} setKeepReleaseSales={setKeepReleaseSales}
        keepReleaseMemo={keepReleaseMemo} setKeepReleaseMemo={setKeepReleaseMemo}
        keepReleaseSaving={keepReleaseSaving} salesPersonNames={salesPersonNames} keepReleaseActions={keepReleaseActions}
        onKeepMemoSave={onKeepMemoSave}
        onKeepReleaseAssign={onKeepReleaseAssign}
        onKeepReleaseCancelVisit={onKeepReleaseCancelVisit}
      />
    </div>
  );
}
